import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileText, Sparkles, Plus, Trash2, Download, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { trackClick } from '@/utils/tracking';

const ZETY_AFFILIATE_LINK = "https://zety.com/resume-builder?ref=edassist";

const ResumeBuilderPage = () => {
  const { user, token } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewHistory, setViewHistory] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    summary: '',
    subjects: '',
    skills: '',
    certifications: '',
    experience: [{ title: '', organization: '', duration: '', description: '' }],
    education: [{ degree: '', institution: '', year: '' }],
    template: 'professional',
  });

  const [zetyLink, setZetyLink] = useState(ZETY_AFFILIATE_LINK);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/resume-builder/history`, { headers, withCredentials: true })
      .then(res => setHistory(res.data.resumes || []))
      .catch(() => {});
    // Fetch configurable Zety link from CMS settings
    axios.get(`${API}/settings/public`).then(res => {
      if (res.data?.zety_affiliate_link) setZetyLink(res.data.zety_affiliate_link);
    }).catch(() => {});
  }, [token, result]);

  const handleGenerate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setGenerating(true);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      const payload = {
        profile: {
          ...form,
          subjects: form.subjects.split(',').map(s => s.trim()).filter(Boolean),
          skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
          certifications: form.certifications.split(',').map(s => s.trim()).filter(Boolean),
        },
        template: form.template,
      };
      const res = await axios.post(`${API}/resume-builder/generate`, payload, { headers, withCredentials: true });
      setResult(res.data);
      toast.success('Resume generated!');
    } catch {
      toast.error('Failed to generate resume');
    }
    setGenerating(false);
  };

  const addExperience = () => setForm(f => ({ ...f, experience: [...f.experience, { title: '', organization: '', duration: '', description: '' }] }));
  const addEducation = () => setForm(f => ({ ...f, education: [...f.education, { degree: '', institution: '', year: '' }] }));
  const removeExperience = (i) => setForm(f => ({ ...f, experience: f.experience.filter((_, idx) => idx !== i) }));
  const removeEducation = (i) => setForm(f => ({ ...f, education: f.education.filter((_, idx) => idx !== i) }));
  const updateExp = (i, field, val) => setForm(f => ({ ...f, experience: f.experience.map((e, idx) => idx === i ? { ...e, [field]: val } : e) }));
  const updateEdu = (i, field, val) => setForm(f => ({ ...f, education: f.education.map((e, idx) => idx === i ? { ...e, [field]: val } : e) }));

  const downloadResume = (content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resume.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="resume-builder-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              AI Resume Builder
            </h1>
            <p className="text-slate-500 mt-1">Create a professional resume powered by AI</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setViewHistory(!viewHistory)} data-testid="resume-history-btn">
              <Clock className="w-4 h-4 mr-2" />
              {viewHistory ? 'Builder' : 'History'}
            </Button>
            <a href={zetyLink} target="_blank" rel="noreferrer" onClick={() => trackClick('zety_resume', zetyLink)}>
              <Button variant="outline" data-testid="zety-link">
                <ExternalLink className="w-4 h-4 mr-2" />
                Advanced (Zety)
              </Button>
            </a>
          </div>
        </div>

        {viewHistory ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Generated Resumes</h2>
            {history.length > 0 ? history.map((r) => (
              <Card key={r.resume_id} data-testid={`resume-${r.resume_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{r.template}</Badge>
                    <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-slate-50 p-3 rounded">{r.generated_content}</pre>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => downloadResume(r.generated_content)}>
                    <Download className="w-3 h-3 mr-1" />Download
                  </Button>
                </CardContent>
              </Card>
            )) : <p className="text-slate-400 py-8 text-center">No resumes generated yet</p>}
          </div>
        ) : result ? (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Generated Resume</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadResume(result.content)} data-testid="download-resume-btn">
                      <Download className="w-4 h-4 mr-2" />Download
                    </Button>
                    <Button size="sm" onClick={() => setResult(null)} data-testid="new-resume-btn">
                      <Plus className="w-4 h-4 mr-2" />New
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <pre className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-6 rounded-lg leading-relaxed" data-testid="resume-output">
                  {result.content}
                </pre>
              </CardContent>
            </Card>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                Want a more polished, template-based resume?{' '}
                <a href={zetyLink} target="_blank" rel="noreferrer" onClick={() => trackClick('zety_resume', zetyLink)} className="underline font-medium">Try Zety Resume Builder</a> for advanced templates and design options.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Form */}
            <Card>
              <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Personal Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="resume-name" /></div>
                  <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="resume-email" /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91-..." className="mt-1" /></div>
                  <div>
                    <Label>Template</Label>
                    <Select value={form.template} onValueChange={(v) => setForm(f => ({ ...f, template: v }))}>
                      <SelectTrigger className="mt-1" data-testid="resume-template"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="modern">Modern</SelectItem>
                        <SelectItem value="academic">Academic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Professional Summary</Label><Textarea value={form.summary} onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))} placeholder="Brief overview of your teaching career..." rows={3} className="mt-1" /></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label>Subjects (comma separated)</Label><Input value={form.subjects} onChange={(e) => setForm(f => ({ ...f, subjects: e.target.value }))} placeholder="Math, Physics" className="mt-1" /></div>
                  <div><Label>Skills (comma separated)</Label><Input value={form.skills} onChange={(e) => setForm(f => ({ ...f, skills: e.target.value }))} placeholder="Classroom Mgmt, Curriculum Design" className="mt-1" /></div>
                  <div><Label>Certifications (comma separated)</Label><Input value={form.certifications} onChange={(e) => setForm(f => ({ ...f, certifications: e.target.value }))} placeholder="B.Ed, CTET" className="mt-1" /></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Experience</CardTitle>
                  <Button variant="outline" size="sm" onClick={addExperience}><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {form.experience.map((exp, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-500">Experience {i + 1}</span>
                      {form.experience.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeExperience(i)}><Trash2 className="w-3 h-3" /></Button>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input placeholder="Job Title" value={exp.title} onChange={(e) => updateExp(i, 'title', e.target.value)} />
                      <Input placeholder="Organization" value={exp.organization} onChange={(e) => updateExp(i, 'organization', e.target.value)} />
                      <Input placeholder="Duration (e.g. 2020-2025)" value={exp.duration} onChange={(e) => updateExp(i, 'duration', e.target.value)} />
                    </div>
                    <Textarea placeholder="Key responsibilities & achievements..." value={exp.description} onChange={(e) => updateExp(i, 'description', e.target.value)} rows={2} />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Education</CardTitle>
                  <Button variant="outline" size="sm" onClick={addEducation}><Plus className="w-3 h-3 mr-1" />Add</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {form.education.map((edu, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <Input placeholder="Degree" value={edu.degree} onChange={(e) => updateEdu(i, 'degree', e.target.value)} className="flex-1" />
                    <Input placeholder="Institution" value={edu.institution} onChange={(e) => updateEdu(i, 'institution', e.target.value)} className="flex-1" />
                    <Input placeholder="Year" value={edu.year} onChange={(e) => updateEdu(i, 'year', e.target.value)} className="w-24" />
                    {form.education.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeEducation(i)}><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Button onClick={handleGenerate} disabled={generating} className="w-full" size="lg" data-testid="generate-resume-btn">
              {generating ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Generating...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" />Generate Resume with AI</>
              )}
            </Button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ResumeBuilderPage;
